/**
 * Readers for the parts of the API the platform does not type.
 *
 * A round result and a game's `config` are opaque to the GSP by design — it
 * carries them between the engine and whoever is drawing the screen without
 * looking inside. That leaves the client holding `unknown`, and these turn it
 * back into something a scene can use: the scene knows what its own game
 * sends, and it is the only thing in the stack that does.
 *
 * Each reader falls back rather than throwing, so a field the engine renamed
 * shows up as a missing number on screen instead of a dead canvas.
 */
type Opaque = Record<string, unknown> | null | undefined;

export function str(source: Opaque, key: string, fallback = ''): string {
  const value = source?.[key];
  return typeof value === 'string' ? value : fallback;
}

export function num(source: Opaque, key: string, fallback = 0): number {
  const value = source?.[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
}

export function bool(source: Opaque, key: string, fallback = false): boolean {
  const value = source?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

export function obj(source: Opaque, key: string): Record<string, unknown> | null {
  const value = source?.[key];
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

export function list(source: Opaque, key: string): unknown[] {
  const value = source?.[key];
  return Array.isArray(value) ? value : [];
}
